import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import GiftStore from "./GiftStore";
import GiftAnimationOverlay from "./GiftAnimationOverlay";
import "../styles/VideoCard.css";

import {
  FaHeart,
  FaCommentDots,
  FaEye,
  FaStar,
  FaShareAlt,
  FaFlag,
  FaTrash,
  FaEllipsisV,
  FaReply,
} from "react-icons/fa";

import axios from "axios";

// ✅ CONFIGURATION DES DEUX PORTES DU SERVEUR
const API_URL = "https://live-evo.com/api";
const MEDIA_URL = "https://live-evo.com/uploads";

const api = axios.create({ baseURL: API_URL });

function VideoCard({ video, currentUserEmail }) {
  const navigate = useNavigate();

  const [comments, setComments] = useState([]);
  const [content, setContent] = useState("");
  const [showComments, setShowComments] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [showMenu, setShowMenu] = useState(false);
  const [showGifts, setShowGifts] = useState(false);
  const [giftAnimation, setGiftAnimation] = useState(null);

  const menuRef = useRef(null);
  const videoRef = useRef(null);
  const startWatchTime = useRef(null);

  const currentUser = JSON.parse(localStorage.getItem("currentUser"));
  const userPseudo = currentUser?.pseudo;

  const isMyVideo =
    video.user_email === currentUserEmail ||
    video.email === currentUserEmail ||
    video.owner_email === currentUserEmail;

  // ==========================================
  // 🎬 TRACKING WATCH TIME (ALGO EVO)
  // ==========================================

  useEffect(() => {
    startWatchTime.current = Date.now();

    return () => {
      if (!video?.id) return;

      const watchTime =
        (Date.now() - startWatchTime.current) / 1000;

      api.post("/videos/watchtime", {
        videoId: video.id,
        watchTime,
      }).catch(() => {});

      // 🔥 Si quitté trop tôt = skip
      if (watchTime < 3) {
        api.post("/videos/skip", {
          videoId: video.id,
        }).catch(() => {});
      }
    };
  }, [video?.id]);

  // ==========================================
  // 🎬 TRACKING REPLAY
  // ==========================================

  useEffect(() => {
    const videoEl = videoRef.current;
    if (!videoEl) return;

    const handleReplay = () => {
      api.post("/videos/replay", {
        videoId: video.id,
      }).catch(() => {});
    };

    videoEl.addEventListener("ended", handleReplay);

    return () => {
      videoEl.removeEventListener("ended", handleReplay);
    };
  }, [video?.id]);

  useEffect(() => {
    const handler = (e) => setGiftAnimation(e.detail);
    window.addEventListener("gift-animation", handler);
    return () => window.removeEventListener("gift-animation", handler);
  }, []);

  // 👁️ VIEW
  useEffect(() => {
    if (!video?.id || !currentUserEmail) return;

    api.post("/videos/views", {
      videoId: video.id,
      userEmail: currentUserEmail,
    }).catch(() => {});
  }, [video?.id, currentUserEmail]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target))
        setShowMenu(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () =>
      document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const loadComments = async () => {
    try {
      const res = await api.get(`/videos/${video.id}/comments`);
      setComments(res.data.items || []);
    } catch {
      console.error("Erreur chargement commentaires");
    }
  };

  const handleToggleComments = () => {
    if (!showComments) loadComments();
    setShowComments((prev) => !prev);
    setContent("");
    setReplyTo(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!content.trim() || !userPseudo) return;

    try {
      await api.post(`/videos/${video.id}/comment`, {
        userEmail: currentUserEmail,
        userPseudo,
        content,
        parentId: replyTo,
      });

      setContent("");
      setReplyTo(null);
      loadComments();
    } catch {
      alert("❌ Erreur envoi commentaire");
    }
  };

  const handleShare = () => {
    navigator.clipboard.writeText(
      `${window.location.origin}/video/${video.id}`
    );

    alert("📋 Lien copié !");
    setShowMenu(false);
  };

  const handleFavorite = () => {
    api.post(`/favorite/${video.id}`);
    alert("⭐ Ajouté aux favoris");
    setShowMenu(false);
  };

  const handleReport = () => {
    api.post(`/report/${video.id}`);
    alert("🚩 Signalement envoyé");
    setShowMenu(false);
  };

  const handleDelete = () => {
    if (window.confirm("🗑️ Supprimer cette vidéo ?")) {
      api.delete(`/videos/${video.id}`);
      alert("✅ Vidéo supprimée");
      window.location.reload();
    }

    setShowMenu(false);
  };

  const handleGift = () => {
    if (!currentUserEmail) {
      alert("❌ Connectez-vous pour envoyer un cadeau");
      return;
    }

    setShowGifts(true);
  };

  const renderReplies = (parentId) =>
    comments
      .filter((c) => c.parent_id === parentId)
      .map((reply) => (
        <div
          key={reply.id}
          className="ms-4 mt-2 border-start ps-3 text-light"
        >
          <strong>@{reply.user_pseudo}</strong> : {reply.content}
        </div>
      ));

  // ==========================================
  // 🎬 CHEMIN VIDÉO
  // ==========================================

  const videoUrl = video?.file_path
    ? `${MEDIA_URL}/${video.file_path.replace(/^\/+/, "")}`
    : null;

  return (
    <>
      <GiftAnimationOverlay animation={giftAnimation} />

      <div
        className="card bg-dark text-light mb-4 position-relative"
        style={{ overflow: "visible" }}
      >
        {videoUrl && (
          <video
            ref={videoRef}
            key={videoUrl}
            className="w-100"
            src={videoUrl}
            controls
            playsInline
            style={{ maxHeight: "400px", objectFit: "cover" }}
          />
        )}

        <div
          className="position-absolute d-flex flex-column align-items-center gap-3"
          style={{ right: 12, top: "20%", zIndex: 999 }}
        >
          <div className="text-center">
            <FaHeart />
            <br />
            {video.likes_count || 0}
          </div>

          <div className="text-center">
            <FaEye />
            <br />
            {video.views_count || 0}
          </div>

          <button
            className="btn btn-sm btn-outline-light rounded-circle"
            onClick={handleToggleComments}
            style={{ width: 32, height: 32 }}
          >
            <FaCommentDots />
          </button>

          <button
            className="btn btn-sm btn-outline-warning rounded-circle"
            onClick={handleGift}
            style={{ width: 32, height: 32 }}
          >
            🎁
          </button>

          <div ref={menuRef} className="position-relative">
            <button
              className="btn btn-sm btn-outline-light rounded-circle"
              onClick={() => setShowMenu(!showMenu)}
              style={{ width: 32, height: 32 }}
            >
              <FaEllipsisV />
            </button>

            {showMenu && (
              <div
                className="bg-white text-dark rounded shadow p-2 position-absolute"
                style={{ top: 40, right: 0, minWidth: 180 }}
              >
                <button
                  className="dropdown-item"
                  onClick={handleShare}
                >
                  <FaShareAlt /> Partager
                </button>

                <button
                  className="dropdown-item"
                  onClick={handleFavorite}
                >
                  <FaStar /> Favori
                </button>

                <button
                  className="dropdown-item"
                  onClick={handleReport}
                >
                  <FaFlag /> Signaler
                </button>

                {isMyVideo && (
                  <button
                    className="dropdown-item text-danger"
                    onClick={handleDelete}
                  >
                    <FaTrash /> Supprimer
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="card-body">
          <h6>{video.title || "Sans titre"}</h6>

          <p className="text-muted">
            Posté par{" "}
            <Link
              to={`/user/${video.user_pseudo}`}
              className="text-light"
            >
              @{video.user_pseudo}
            </Link>
          </p>

          {showComments && (
            <div className="mt-3">
              <h6 className="text-info">💬 Commentaires</h6>

              {comments.length === 0 ? (
                <p className="text-muted">Aucun commentaire</p>
              ) : (
                comments
                  .filter((c) => !c.parent_id)
                  .map((comment) => (
                    <div key={comment.id} className="mb-2">
                      <strong>@{comment.user_pseudo}</strong> :{" "}
                      {comment.content}

                      <button
                        className="btn btn-sm btn-outline-info ms-2"
                        onClick={() => setReplyTo(comment.id)}
                      >
                        <FaReply /> Répondre
                      </button>

                      {renderReplies(comment.id)}
                    </div>
                  ))
              )}

              {currentUserEmail && (
                <form
                  onSubmit={handleSubmit}
                  className="mt-2"
                >
                  <textarea
                    className="form-control bg-secondary text-white"
                    rows="2"
                    value={content}
                    onChange={(e) =>
                      setContent(e.target.value)
                    }
                  />

                  <button
                    type="submit"
                    className="btn btn-info mt-2"
                  >
                    Envoyer
                  </button>
                </form>
              )}
            </div>
          )}
        </div>
      </div>

      {showGifts && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center"
          style={{
            background: "rgba(0,0,0,0.7)",
            zIndex: 9999,
          }}
          onClick={() => setShowGifts(false)}
        >
          <div
            className="bg-dark text-light rounded p-3"
            style={{ width: "90%", maxWidth: 520 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="d-flex justify-content-between mb-2">
              <h5>🎁 Envoyer un cadeau</h5>

              <button
                className="btn btn-sm btn-outline-light"
                onClick={() => setShowGifts(false)}
              >
                ✖
              </button>
            </div>

            <GiftStore
              receiverEmail={
                video.user_email ||
                video.email ||
                video.owner_email
              }
              context="video"
              videoId={video.id}
              onNotEnoughCoins={() => {
                setShowGifts(false);
                navigate("/packs");
              }}
              onSent={() => setShowGifts(false)}
            />
          </div>
        </div>
      )}
    </>
  );
}

export default VideoCard;
